/**
 * iter318 wave 17 — Google sign-in handoff landing page.
 *
 * Mounted at /auth/google-continue on every tenant subdomain. Reads
 * the ?token=… query param and POSTs it to /api/auth/google/handoff.
 * On success, the backend sets the app JWT cookies and this page
 * hard-redirects to the tenant dashboard so the AuthContext picks
 * up the fresh session.
 *
 * Also mounted at /auth/callback for the raw session_id flow on
 * tenant hosts (when the user clicks the Google button directly on
 * a tenant login screen instead of the global helper).
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

// iter318 wave 20 (2026-02) — POST same-origin so tenant subdomains
// (e.g. dascricket.vyomos.io) don't hit `www.vyomos.io` and get
// CORS-blocked. `REACT_APP_BACKEND_URL` is a build-time constant
// baked in at deploy time, but every tenant proxies `/api/*` to the
// same backend via Cloudflare — same-origin is always the correct
// choice at runtime. Keeps `REACT_APP_BACKEND_URL` as an SSR/build
// fallback for future server-rendered contexts.
const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL);

// iter318 wave 20b (2026-02) — Seed localStorage with the tokens
// returned by the handoff endpoint. HttpOnly cookies alone are not
// enough because `AuthContext.initAuth` short-circuits when
// `localStorage.token` is empty and never calls `/auth/me` — meaning
// the user would land on `/` still appearing logged-out. Mirror the
// key names + `authStorage` flag used by `AuthContext.login()`.
function persistAuth(data) {
  if (!data || !data.access_token) return;
  try {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('authStorage', 'local');
    if (data.refresh_token) {
      localStorage.setItem('refreshToken', data.refresh_token);
    }
  } catch (e) {
    console.warn('[handoff] failed to persist token to localStorage', e);
  }
}

export default function GoogleContinue({ mode = 'tenant' }) {
  const [message, setMessage] = useState('Signing you in…');
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const q = new URLSearchParams(window.location.search);
    const token = q.get('token');

    // Also support the direct `#session_id=…` return from Emergent OAuth
    // in case the user clicked the Google button on a tenant login screen.
    const hash = window.location.hash || '';
    const sessionId = hash.includes('session_id=')
      ? new URLSearchParams(hash.replace(/^#/, '')).get('session_id')
      : null;

    if (mode === 'super_admin' && token) {
      finishSuperAdminHandoff(token);
    } else if (token) {
      finishHandoff(token);
    } else if (sessionId) {
      finishTenantLogin(sessionId);
    } else {
      setError('Missing sign-in token. Please try signing in again.');
    }
  }, [mode]);

  const finishHandoff = async (token) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/google/handoff`,
        { token },
        { withCredentials: true },
      );
      persistAuth(data);
      setMessage('Success! Redirecting…');
      // Hard reload so AuthContext picks up the new session.
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign-in failed. Please try again.');
    }
  };

  // iter318 wave 21 — Platform-scope super-admin handoff. Runs on
  // www.vyomos.io/auth/super-admin-continue, hits the global endpoint
  // that issues the session directly on the platform host (no tenant),
  // and lands the operator on the /super-admin console instead of a
  // random tenant subdomain.
  const finishSuperAdminHandoff = async (token) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/global/super-admin-handoff`,
        { token },
        { withCredentials: true },
      );
      persistAuth(data);
      setMessage('Success! Opening the super-admin console…');
      window.location.href = '/super-admin';
    } catch (err) {
      setError(
        err.response?.data?.detail
          || 'Super-admin sign-in failed. Please try again.',
      );
    }
  };

  const finishTenantLogin = async (sessionId) => {
    try {
      // Clear the fragment so a refresh doesn't retry.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      const { data } = await axios.post(
        `${API}/api/auth/google/tenant-login`,
        { session_id: sessionId },
        { withCredentials: true },
      );
      persistAuth(data);
      setMessage('Success! Redirecting…');
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign-in failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FBFF] px-6" data-testid="google-continue">
      <div className="max-w-md w-full bg-white border border-[#DCE6F0] rounded-3xl p-8 shadow-sm text-center">
        <div className="h-12 w-12 mx-auto mb-4 rounded-full bg-[#EAF5FF] flex items-center justify-center">
          <div className={error ? 'h-6 w-6 rounded-full bg-rose-500' : 'h-6 w-6 rounded-full bg-[#1677FF] animate-pulse'} />
        </div>
        {error ? (
          <>
            <h1 className="text-xl font-bold text-[#10233F] mb-2" data-testid="google-continue-error">Sign-in didn&apos;t complete</h1>
            <p className="text-sm text-[#566579] mb-6">{error}</p>
            <a href="/" className="inline-block px-5 py-2 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white text-sm font-semibold">
              Back to sign-in
            </a>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-[#10233F] mb-2">{message}</h1>
            <p className="text-sm text-[#566579]">One moment — we&apos;re verifying your account.</p>
          </>
        )}
      </div>
    </div>
  );
}
